import type { JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { StatusBadge } from '@/components/dashboard/StatusBadge';
import { UnitDeleteButton } from '@/components/dashboard/units/UnitDeleteButton';
import { PillButton } from '@/components/entity/PillButton';
import { PUBLIC_READINESS_LABELS } from '@/components/public/properties';
import type { UnitSummary } from '@/lib/types/property';
import {
  formatFloorLevelLabel,
  formatSquareFeet,
  formatUnitLabel,
} from '@/lib/utils/propertyFormatting';
import {
  formatMoveInReadinessLabel,
  formatRent,
  readinessTone,
} from '@/routes/dashboard/propertyDetail/propertyUnitsListFormatting';

interface PropertyUnitsListProps {
  propertyId: string;
  units: UnitSummary[];
}

export function PropertyUnitsList({ propertyId, units }: PropertyUnitsListProps): JSX.Element {
  const navigate = useNavigate();

  const handleAddUnit = () => {
    navigate(`/dashboard/properties/${propertyId}/units/new`);
  };

  const handleRowClick = (event: React.MouseEvent<HTMLTableRowElement>, unitId: string) => {
    const target = event.target as HTMLElement | null;
    if (target?.closest('button, a, input, textarea, select')) {
      return;
    }

    navigate(`/dashboard/properties/${propertyId}/units/${unitId}`);
  };

  const handleRowKeyDown = (event: React.KeyboardEvent<HTMLTableRowElement>, unitId: string) => {
    const target = event.target as HTMLElement | null;
    if (target?.closest('button, a, input, textarea, select')) {
      return;
    }

    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    navigate(`/dashboard/properties/${propertyId}/units/${unitId}`);
  };

  if (units.length === 0) {
    return (
      <section className="flex flex-col gap-4 rounded-3xl border border-surface-subtle bg-white p-6 shadow-sm">
        <header className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-text-primary/60">
              Units
            </h2>
            <p className="mt-1 text-sm text-text-primary/70">
              No units yet. Create the first unit for this property.
            </p>
          </div>
          <PillButton onClick={handleAddUnit}>Add unit</PillButton>
        </header>
      </section>
    );
  }

  return (
    <section className="flex flex-col gap-4 rounded-3xl border border-surface-subtle bg-white p-6 shadow-sm">
      <header className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-text-primary/60">
            Units
          </h2>
          <p className="mt-1 text-sm text-text-primary/70">
            {units.length === 1
              ? '1 unit on record.'
              : `${units.length.toString()} units on record.`}
          </p>
        </div>
        <PillButton onClick={handleAddUnit}>Add unit</PillButton>
      </header>

      <div className="overflow-x-auto">
        <table className="divide-surface-muted min-w-full divide-y text-sm text-text-primary">
          <thead className="bg-surface-muted/40 text-xs uppercase tracking-wide text-text-primary/60">
            <tr>
              <th scope="col" className="px-4 py-3 text-left font-semibold">
                Unit
              </th>
              <th scope="col" className="px-4 py-3 text-left font-semibold">
                Area
              </th>
              <th scope="col" className="px-4 py-3 text-left font-semibold">
                Move-in readiness
              </th>
              <th scope="col" className="px-4 py-3 text-left font-semibold">
                Availability status
              </th>
              <th scope="col" className="px-4 py-3 text-left font-semibold">
                Display on public website?
              </th>
              <th scope="col" className="px-4 py-3 text-left font-semibold">
                Rent
              </th>
              <th scope="col" className="px-4 py-3 text-left font-semibold">
                Realtor outreach emails
              </th>
              <th scope="col" className="px-4 py-3 text-left font-semibold">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-surface-muted/80 divide-y">
            {units.map((unit) => (
              <tr
                key={unit.id}
                className="cursor-pointer align-top hover:bg-brand-primary/5"
                tabIndex={0}
                role="link"
                aria-label={`View unit ${formatUnitLabel(unit)}`}
                onClick={(event) => {
                  handleRowClick(event, unit.id);
                }}
                onKeyDown={(event) => {
                  handleRowKeyDown(event, unit.id);
                }}
              >
                <td className="px-4 py-4">
                  <div className="flex flex-col gap-1">
                    <p className="text-sm font-semibold text-text-primary">
                      {formatUnitLabel(unit)}
                    </p>
                    <p className="text-xs text-text-primary/70">
                      {formatFloorLevelLabel(unit.floorLevel)}
                    </p>
                  </div>
                </td>
                <td className="px-4 py-4">{formatSquareFeet(unit.areaSf)}</td>
                <td className="px-4 py-4">
                  <span className="text-xs text-text-primary/70">
                    {formatMoveInReadinessLabel(unit)}
                  </span>
                </td>
                <td className="px-4 py-4">
                  <StatusBadge
                    tone={readinessTone(unit.availability.status)}
                    label={PUBLIC_READINESS_LABELS[unit.availability.status]}
                  />
                </td>
                <td className="px-4 py-4">
                  <StatusBadge
                    tone={unit.displayOnPublicWebsite ? 'success' : 'neutral'}
                    label={unit.displayOnPublicWebsite ? 'Yes' : 'No'}
                  />
                </td>
                <td className="px-4 py-4">{formatRent(unit)}</td>
                <td className="px-4 py-4">
                  <StatusBadge
                    tone={unit.published ? 'success' : 'neutral'}
                    label={unit.published ? 'Yes' : 'No'}
                  />
                </td>
                <td className="px-4 py-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <PillButton
                      size="sm"
                      variant="secondary"
                      onClick={() => {
                        navigate(`/dashboard/properties/${propertyId}/units/${unit.id}`);
                      }}
                    >
                      View
                    </PillButton>
                    <PillButton
                      size="sm"
                      variant="secondary"
                      onClick={() => {
                        navigate(`/dashboard/properties/${propertyId}/units/${unit.id}/edit`);
                      }}
                    >
                      Edit
                    </PillButton>
                    <UnitDeleteButton
                      propertyId={propertyId}
                      unit={unit}
                      mode="icon"
                      onDeleted={() => {
                        navigate(`/dashboard/properties/${propertyId}`);
                      }}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
